import * as AuthSession from 'expo-auth-session'
import { getAuth, signInWithCustomToken } from 'firebase/auth'
import { useEffect, useState } from 'react'
import { MasterAPI } from '../api/master'
import { oauthConfig, oauthDiscovery } from '../constants/oauth'
import { isWeb } from '../constants/platform'
import { $adapteeAPI, $masterAPI } from '../http'
import * as AuthSessionProvider from '../providers/expo-auth-session/expo-auth-session'
import { setLicense } from '../redux/reducers/license'
import { OAuthService } from '../services/oauth'
import { IOAuthTokensForStorage } from '../types/token'
import { useAppDispatch } from './redux'

const handleAuth = async (tokens: IOAuthTokensForStorage) => {
    await OAuthService.setTokensToStorage({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken!,
        expiresIn: tokens.expiresIn!,
        issuedAt: tokens.issuedAt,
    })

    $masterAPI.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`

    const fbToken = await MasterAPI.fetchFirebaseToken()

    const auth = getAuth()

    await signInWithCustomToken(auth, fbToken)

    const license = await MasterAPI.fetchLicense()

    $adapteeAPI.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`
    $adapteeAPI.defaults.baseURL = `${license.api_url}/api/digibon/v1`

    return license
}

export const useAuth = () => {
    const dispatch = useAppDispatch()

    const [isOAuthRefresh, setIsOAuthRefresh] = useState(true)

    const [request, result, promptAsync] = AuthSession.useAuthRequest(oauthConfig, oauthDiscovery)

    useEffect(() => {
        ;(async () => {
            try {
                const tokens = await OAuthService.getTokensFromStorage()

                if (!tokens) {
                    setIsOAuthRefresh(false)

                    return
                }

                const freshTokens = await AuthSessionProvider.refreshAsync(
                    { clientId: oauthConfig.clientId, refreshToken: tokens.refreshToken },
                    oauthDiscovery
                )

                const license = await handleAuth({
                    accessToken: freshTokens.accessToken,
                    refreshToken: freshTokens.refreshToken!,
                    expiresIn: freshTokens.expiresIn!,
                    issuedAt: freshTokens.issuedAt,
                })

                dispatch(
                    setLicense({
                        token: license.fbrd_token,
                        url: license.api_url,
                        name: license.licence_name,
                        language: license.language,
                    })
                )
            } catch (e) {
                OAuthService.removeTokensFromStorage()

                setIsOAuthRefresh(false)

                if (!__DEV__) return

                console.error(e)
            }
        })()
    }, [dispatch])

    useEffect(() => {
        if (isOAuthRefresh || !request || isWeb) return

        promptAsync()
    }, [isOAuthRefresh, request, promptAsync])

    useEffect(() => {
        if (!request || !result) return

        if (result.type === 'dismiss' && !isWeb) {
            promptAsync()

            return
        }

        if (result.type === 'success') {
            ;(async () => {
                const tokens = await OAuthService.fetchTokens(
                    result.params.code,
                    request.codeVerifier!
                )

                const license = await handleAuth({
                    accessToken: tokens.accessToken,
                    refreshToken: tokens.refreshToken!,
                    expiresIn: tokens.expiresIn!,
                    issuedAt: tokens.issuedAt,
                })

                dispatch(
                    setLicense({
                        token: license.fbrd_token,
                        url: license.api_url,
                        name: license.licence_name,
                        language: license.language,
                    })
                )
            })()
        }
    }, [request, result, dispatch, promptAsync])

    return [isOAuthRefresh, result, promptAsync] as const
}
